<script setup lang="ts">
import type { TooltipRootEmits, TooltipRootProps } from 'radix-vue'
import {
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from 'radix-vue'
import { computed, reactive, toRefs } from 'vue'

interface Props extends TooltipRootProps {
  label?: string | string[]
  asChild?: boolean
  sideOffset?: number
  side?: 'bottom' | 'left' | 'top' | 'right'
  showArrow?: boolean
  delayDuration?: number
  triggerClass?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  sideOffset: 5,
  side: 'top',
  showArrow: true,
  delayDuration: 100,
  disabled: false,
})
defineEmits<TooltipRootEmits>()

const { sideOffset, label, asChild, side, showArrow, delayDuration, disabled } = toRefs(props)

const staticProps = reactive({
  collisionPadding: { left: 24, right: 24 },
  arrowSize: 10,
})

const arrowHeight = computed(() => Math.round(staticProps.arrowSize * 0.55))
const labelLines = computed(() => {
  if (Array.isArray(label.value)) {
    return label.value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
  }
  if (typeof label.value === 'string' && label.value.trim().length > 0) return [label.value]
  return []
})
const hasMultipleLines = computed(() => labelLines.value.length > 1)
const isDisabled = computed(() => disabled.value || labelLines.value.length === 0)
</script>

<template>
  <TooltipProvider :delay-duration="delayDuration" :disabled="isDisabled">
    <TooltipRoot>
      <TooltipTrigger class="focus:outline-none" :class="[triggerClass]" :as-child="asChild">
        <slot />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent
          :side="side"
          :side-offset="sideOffset"
          :collision-padding="staticProps.collisionPadding"
          class="tooltip-content"
        >
          <div v-if="hasMultipleLines" class="flex flex-col gap-1 max-w-96 text-left whitespace-normal">
            <span v-for="(line, index) in labelLines" :key="`${line}-${index}`" class="leading-4">
              {{ line }}
            </span>
          </div>
          <template v-else>
            {{ labelLines[0] }}
          </template>
          <TooltipArrow
            v-if="showArrow"
            as-child
            :width="staticProps.arrowSize"
            :height="arrowHeight"
          >
            <svg
              class="tooltip-arrow"
              :width="staticProps.arrowSize"
              :height="arrowHeight"
              viewBox="0 0 10 6"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d="M0 0 L5 6 L10 0 Z" />
            </svg>
          </TooltipArrow>
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  </TooltipProvider>
</template>

<style lang="postcss">
.tooltip-content {
  @apply antialiased relative z-50 inline-flex min-h-6 rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg border border-gray-800 font-medium dark:bg-gray-900 dark:text-white dark:border-gray-800 dark:shadow-xl dark:shadow-black/20 max-w-96 text-center;
  transition:
    opacity 150ms ease-out,
    transform 150ms ease-out;
  will-change: transform, opacity;
  --tooltip-bg: 17 24 39;
  --tooltip-bg-alpha: 1;
  --tooltip-border: 31 41 55;
  --tooltip-border-alpha: 1;
}

.tooltip-content[data-side='bottom'] {
  animation: slideDownAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
}
.tooltip-content[data-side='left'] {
  animation: slideLeftAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
}
.tooltip-content[data-side='right'] {
  animation: slideRightAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
}
.tooltip-content[data-side='top'] {
  animation: slideUpAndFade 400ms cubic-bezier(0.16, 1, 0.3, 1);
}

.tooltip-content[data-state='closed'] {
  animation: hide 100ms ease-in;
}

.tooltip-content .tooltip-arrow {
  display: block;
  flex-shrink: 0;
}

.tooltip-content .tooltip-arrow path {
  fill: rgb(var(--tooltip-bg) / var(--tooltip-bg-alpha));
  stroke: rgb(var(--tooltip-border) / var(--tooltip-border-alpha));
  stroke-width: 1;
  stroke-linejoin: miter;
  stroke-linecap: butt;
  vector-effect: non-scaling-stroke;
}

/* Slightly overlap the arrow with the tooltip border to avoid seams */
.tooltip-content[data-side='top'] .tooltip-arrow {
  transform: translateY(0.5px);
}
.tooltip-content[data-side='bottom'] .tooltip-arrow {
  transform: translateY(-0.5px);
}
.tooltip-content[data-side='left'] .tooltip-arrow {
  transform: translateX(0.5px);
}
.tooltip-content[data-side='right'] .tooltip-arrow {
  transform: translateX(-0.5px);
}

.dark .tooltip-content {
  --tooltip-bg: 17 24 39;
  --tooltip-bg-alpha: 1;
  --tooltip-border: 31 41 55;
  --tooltip-border-alpha: 1;
}

@keyframes hide {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
</style>
